/**
 * Wayja Brand Configuration
 * 
 * This file contains all brand-related configuration including colors, fonts, and assets.
 * 
 * IMPORTANT: When updating colors, you must also update tailwind.config.js to keep
 * Tailwind CSS classes in sync with these values.
 * 
 * To update the brand:
 * 1. Colors: Update both this file AND tailwind.config.js
 * 2. Fonts: Update this file, tailwind.config.js, and src/index.css @font-face
 * 3. Logos: Update this file and replace files in /public/images/
 */

// =============================================================================
// COLORS
// =============================================================================

export const colors = {
  // Primary brand colors
  primary: {
    black: '#060a0b',
    darkTeal: '#1f4a46',
    teal: '#5cb3ad',        // Main brand color
  },
  
  // Secondary colors
  secondary: {
    lightTeal: '#8ed6c9',
    slate: '#3e5c73',
    coral: '#e35c4a',       // Accent/action color
    green: '#b7e7a8',
    olive: '#a4ac02',
  },
  
  // Tertiary colors
  tertiary: {
    gray: '#c5d5d2',
    darkSlate: '#22333b',
    cream: '#f3f2e1',
    orange: '#f4a261',
    cyan: '#00c2ba',
  },
} as const;

// Chart colors - ordered for maximum visual distinction in data visualization
// Following the brand color scheme from primary → secondary → tertiary
export const chartColors = [
  colors.primary.teal,       // #5cb3ad - Primary teal
  colors.secondary.coral,    // #e35c4a - Coral (contrasts well)
  colors.secondary.slate,    // #3e5c73 - Slate blue
  colors.secondary.green,    // #b7e7a8 - Light green
  colors.tertiary.orange,    // #f4a261 - Orange
  colors.secondary.olive,    // #a4ac02 - Olive/yellow-green
  colors.tertiary.cyan,      // #00c2ba - Cyan
  colors.secondary.lightTeal,// #8ed6c9 - Light teal
  colors.tertiary.darkSlate, // #22333b - Dark slate
  colors.primary.darkTeal,   // #1f4a46 - Dark teal
  colors.tertiary.gray,      // #c5d5d2 - Gray
  colors.tertiary.cream,     // #f3f2e1 - Cream
];

// =============================================================================
// FONTS
// =============================================================================

export const fonts = {
  // Primary font family
  primary: 'Outfit',
  
  // Font weights
  weights: {
    regular: 400,
    semibold: 600,
    extrabold: 800,
  },
  
  // Font files (relative to public folder)
  files: {
    regular: '/fonts/Outfit-Regular.ttf',
    semibold: '/fonts/Outfit-SemiBold.ttf',
    extrabold: '/fonts/Outfit-ExtraBold.ttf',
  },
} as const;

// =============================================================================
// LOGOS & ASSETS
// =============================================================================

export const assets = {
  // Logo files (relative to public folder)
  logos: {
    linear: '/images/logo-linear.svg',      // Full logo with text
    icon: '/images/icon.png',               // Icon only (for favicon, thumbnails)
  },
  
  // Favicon
  favicon: '/favicon-32x32.png',
} as const;

// =============================================================================
// TAILWIND COLOR MAPPING
// For use in tailwind.config.js
// =============================================================================

export const tailwindColors = {
  'wayja-black': colors.primary.black,
  'wayja-dark-teal': colors.primary.darkTeal,
  'wayja-teal': colors.primary.teal,
  'wayja-light-teal': colors.secondary.lightTeal,
  'wayja-slate': colors.secondary.slate,
  'wayja-coral': colors.secondary.coral,
  'wayja-green': colors.secondary.green,
  'wayja-olive': colors.secondary.olive,
  'wayja-gray': colors.tertiary.gray,
  'wayja-dark-slate': colors.tertiary.darkSlate,
  'wayja-cream': colors.tertiary.cream,
  'wayja-orange': colors.tertiary.orange,
  'wayja-cyan': colors.tertiary.cyan,
};

// =============================================================================
// HELPER EXPORTS
// =============================================================================

// Main brand color (for quick access)
export const BRAND_COLOR = colors.primary.teal;
export const ACCENT_COLOR = colors.primary.teal;

// Default export for convenience
const brand = {
  colors,
  chartColors,
  fonts,
  assets,
  tailwindColors,
  BRAND_COLOR,
  ACCENT_COLOR,
};

export default brand;
